import { safeGetItem, safeSetItem, safeSetJson } from "@/lib/storage/safe-storage";
const UTM_STORAGE_KEY = "panda_utm";
const UTM_ANON_ID_KEY = "panda_anon_id";

const UTM_PARAMS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
] as const;

export interface UtmData {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
  referrer?: string;
  landing_page?: string;
  anonymous_id: string;
  first_seen_at: string;
}

function generateAnonId(): string {
  return crypto.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * Captures UTM params from the current URL on first visit.
 * Uses first-touch attribution — never overwrites existing data.
 * Call this on every page load (landing page, referral redirect, etc).
 */
export function captureUtm(): UtmData {
  // Return existing attribution if already captured
  const existing = getStoredUtm();
  if (existing) return existing;

  const params = new URLSearchParams(window.location.search);

  const utmData: UtmData = {
    anonymous_id: getOrCreateAnonId(),
    first_seen_at: new Date().toISOString(),
    landing_page: window.location.pathname + window.location.search,
    referrer: document.referrer || undefined,
  };

  for (const key of UTM_PARAMS) {
    const val = params.get(key);
    if (val) utmData[key] = val;
  }

  safeSetJson(UTM_STORAGE_KEY, utmData);
  return utmData;
}

/**
 * Gets stored UTM data, or null if not yet captured.
 */
export function getStoredUtm(): UtmData | null {
  try {
    const raw = localStorage.getItem(UTM_STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as UtmData;
  } catch {
    return null;
  }
}

/**
 * Gets or creates a persistent anonymous ID for pre-signup tracking.
 */
export function getOrCreateAnonId(): string {
  let anonId = safeGetItem(UTM_ANON_ID_KEY);
  if (!anonId) {
    anonId = generateAnonId();
    safeSetItem(UTM_ANON_ID_KEY, anonId);
  }
  return anonId;
}

/**
 * Sends captured UTM data to the server.
 * Call after captureUtm() on first visit.
 */
export async function syncUtmToServer(utmData: UtmData): Promise<void> {
  try {
    await fetch("/api/attribution", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(utmData),
    });
  } catch {
    // Silently fail — attribution is non-blocking
  }
}

/**
 * Links the anonymous UTM record to a user after signup.
 * Call from the signup flow after successful registration.
 */
export async function linkUtmToUser(userId: string): Promise<void> {
  const utmData = getStoredUtm();
  if (!utmData) return;

  try {
    await fetch("/api/attribution", {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        anonymous_id: utmData.anonymous_id,
        user_id: userId,
      }),
    });
  } catch {
    // Silently fail
  }
}
